import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Menu,
  ArrowRight,
  Brain,
  Cloud,
  Code,
  Shield,
  TrendingUp,
  Zap,
  Sparkles
} from 'lucide-react';
import Logo from './Logo';

const FullscreenNav = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const solutions = [
    { name: 'AI & Machine Learning', path: '/solutions#ai-ml', icon: <Brain className="w-5 h-5" /> },
    { name: 'Cloud & Digital Transformation', path: '/solutions#cloud', icon: <Cloud className="w-5 h-5" /> },
    { name: 'Custom Software Development', path: '/solutions#software', icon: <Code className="w-5 h-5" /> },
    { name: 'IT Consulting', path: '/solutions#consulting', icon: <Zap className="w-5 h-5" /> },
    { name: 'Cybersecurity', path: '/solutions#security', icon: <Shield className="w-5 h-5" /> },
    { name: 'Marketing Intelligence', path: '/marketing', icon: <TrendingUp className="w-5 h-5" /> }
  ];

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <>
      {/* Top Bar */}
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className={`fixed w-full z-40 transition-all duration-500 ${scrolled || isOpen
          ? 'bg-black/90 backdrop-blur-2xl shadow-2xl shadow-cyan-400/20 border-b border-cyan-400/30'
          : 'bg-gradient-to-b from-black/20 via-black/10 to-transparent backdrop-blur-sm'
          }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <motion.div
              className="flex-shrink-0"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <Link to="/">
                <Logo size="default" animated={true} />
              </Link>
            </motion.div>

            {/* Controls */}
            <div className="flex items-center">
              {/* Menu Button */}
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className="p-3 rounded-xl bg-gradient-to-br from-gray-800/60 to-gray-900/60 text-cyan-400 hover:text-cyan-300 backdrop-blur-xl border border-gray-700/50 hover:border-cyan-400/50 transition-all duration-300 relative z-50"
                onClick={() => setIsOpen(!isOpen)}
                aria-label="Toggle menu"
              >
                <motion.div
                  initial={false}
                  animate={{ rotate: isOpen ? 90 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {isOpen ? <X size={20} /> : <Menu size={20} />}
                </motion.div>
              </motion.button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Fullscreen Navigation Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
            className="fixed inset-0 z-50 bg-black"
          >
            {/* Background with texture/pattern */}
            <div
              className="absolute inset-0 opacity-10"
              style={{
                backgroundImage: `
                  radial-gradient(circle at 25% 25%, rgba(34, 211, 238, 0.1) 0%, transparent 50%),
                  radial-gradient(circle at 75% 75%, rgba(168, 85, 247, 0.1) 0%, transparent 50%),
                  radial-gradient(circle, rgba(14, 165, 233, 0.05) 1px, transparent 1px)
                `,
                backgroundSize: '100% 100%, 100% 100%, 60px 60px'
              }}
            />

            <div className="relative z-10 h-full flex flex-col lg:flex-row overflow-y-auto">
              {/* Left Side - Hero Typography */}
              <div className="hidden lg:flex flex-1 flex-col justify-center items-start px-8 md:px-16 lg:px-24">
                <motion.div
                  initial={{ opacity: 0, x: -100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="max-w-2xl"
                >
                  <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extralight leading-tight text-white mb-8">
                    <span className="block">INTELLIGENCE</span>
                    <span
                      className="block font-light"
                      style={{
                        backgroundImage: 'linear-gradient(to right, rgb(103, 232, 249), rgb(96, 165, 250), rgb(168, 85, 247))',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text'
                      }}
                    >
                      UNLEASHED
                    </span>
                    <span className="block text-xl sm:text-2xl md:text-3xl font-extralight text-gray-400 mt-4 tracking-wider">
                      FOR THE FUTURE
                    </span>
                  </h1>

                  <motion.p
                    className="text-lg md:text-xl text-gray-300 font-light leading-relaxed tracking-wide max-w-lg"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 }}
                  >
                    Transform your Business with AI-powered solutions that scale beyond imagination.
                  </motion.p>
                </motion.div>
              </div>

              {/* Right Side - Navigation Menu */}
              <motion.div
                initial={{ opacity: 0, y: 100 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 100 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="w-full lg:max-w-sm bg-gradient-to-b from-gray-900/95 to-black/95 backdrop-blur-2xl lg:border-l border-cyan-400/20 flex flex-col"
              >
                {/* Menu Header */}
                <div className="p-4 sm:p-6 lg:p-8 border-b border-gray-800 flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-light tracking-wider text-cyan-400 mb-2">NAVIGATE</h2>
                    <p className="text-sm text-gray-500 font-light">Explore our solutions</p>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    className="p-2 rounded-lg bg-gray-800/50 text-cyan-400 hover:text-cyan-300 transition-colors"
                    onClick={() => setIsOpen(false)}
                    aria-label="Close menu"
                  >
                    <X size={20} />
                  </motion.button>
                </div>

                {/* Navigation Items */}
                <div className="flex-1 overflow-y-auto">
                  <div className="p-8 space-y-8">
                    {/* Main Navigation */}
                    <div>
                      <h3 className="text-sm font-light tracking-widest text-gray-400 mb-4 uppercase">Main</h3>
                      <nav className="space-y-1">
                        {[
                          { name: 'Home', path: '/' },
                          { name: 'About', path: '/about' },
                          { name: 'Products', path: '/products' },
                          { name: 'Projects', path: '/projects' },
                          { name: 'Industries', path: '/industries' },
                          { name: 'Careers', path: '/careers' }
                        ].map((item, idx) => (
                          <motion.div
                            key={item.name}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.3 + idx * 0.08 }}
                          >
                            <Link
                              to={item.path}
                              className={`group flex items-center justify-between py-3 px-4 rounded-xl transition-all duration-300 ${isActive(item.path)
                                ? 'bg-gradient-to-r from-cyan-500/20 to-purple-600/20 text-cyan-300 border border-cyan-400/40'
                                : 'text-gray-300 hover:text-cyan-300 hover:bg-gradient-to-r hover:from-cyan-500/10 hover:to-purple-600/10'
                                }`}
                            >
                              <span className="font-light tracking-wide">{item.name}</span>
                              <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-all transform translate-x-2 group-hover:translate-x-0" />
                            </Link>
                          </motion.div>
                        ))}
                      </nav>
                    </div>

                    {/* Solutions */}
                    <div>
                      <h3 className="text-sm font-light tracking-widest text-gray-400 mb-4 uppercase flex items-center gap-2">
                        <Sparkles className="w-4 h-4" />
                        Solutions
                      </h3>
                      <nav className="space-y-1">
                        {solutions.map((solution, idx) => (
                          <motion.div
                            key={solution.name}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.6 + idx * 0.08 }}
                          >
                            <Link
                              to={solution.path}
                              className="group flex items-center gap-3 py-3 px-4 rounded-xl text-sm text-gray-400 hover:text-cyan-300 hover:bg-gradient-to-r hover:from-cyan-500/10 hover:to-purple-600/10 transition-all duration-300"
                            >
                              <span className="text-cyan-400 group-hover:text-cyan-300 transition-colors">
                                {solution.icon}
                              </span>
                              <span className="flex-1 font-light tracking-wide">{solution.name}</span>
                              <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-all transform translate-x-2 group-hover:translate-x-0" />
                            </Link>
                          </motion.div>
                        ))}
                      </nav>
                    </div>

                    {/* Contact Section */}
                    <div className="pt-4 border-t border-gray-800">
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.9 }}
                      >
                        <Link
                          to="/contact"
                          className="block w-full bg-gradient-to-r from-cyan-400 to-purple-600 text-white px-6 py-4 rounded-2xl font-light tracking-wide text-center hover:shadow-2xl hover:shadow-cyan-400/40 transition-all duration-300 border border-cyan-400/40 group"
                        >
                          <span className="flex items-center justify-center gap-2">
                            Get Started
                            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                          </span>
                        </Link>
                      </motion.div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default FullscreenNav;